import { useEffect, useRef } from 'react';

import {
  getDependents,
  getDependentsWithIndexes,
  runDependentValidators,
  runDynamicDependents,
  runDynamicDependentsForHiddenUpdates,
  runHiddenDependentsChained,
} from 'helpers/candidate_application/dependent-field-processing';
import { readValueByName, upsertValueByName } from 'helpers/field-paths';
import { useDispatch, useSelector } from 'react-redux';

import {
  recalculateIdRequirements,
  selectCandidateApplication,
  updateCandidateSections,
  updateHiddenValues,
} from 'api/candidate-application-slice';

// Dynamic dependency processing - watch for field changes and process dependencies
export function useOnChangeDependentProcessing(
  watch,
  allValues,
  currentPagePrefixes,
  trigger,
  setError,
  clearErrors,
) {
  const dispatch = useDispatch();
  const { candidateFields } = useSelector(selectCandidateApplication);

  // Track if we're in initial processing phase to avoid triggering validation
  const isInitialProcessing = useRef(true);

  const {
    hiddenFields = {},
    fieldPathIndex = {},
    wrapperValidators = {},
    dynamicDependencyCodes = {},
    hiddenDependencyCodes = {},
    validationDependencyCodes = {},
    idRequirementsDependencyCodes = [],
    templateSources = {},
  } = candidateFields || {};

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (!name) return;

      if (name.includes('.')) {
        const parentName = name.split('.')[0];
        clearErrors(parentName);
      }

      // Skip validation during initial processing to prevent immediate validation errors
      // IMPORTANT: Don't trigger wrapper fields - they use manual setError, not RHF validation
      // Triggering them would clear manual errors
      const isWrapperField = wrapperValidators?.[name];
      if (!isInitialProcessing.current && !isWrapperField) {
        trigger(name);
      }

      const prevSnapshot = allValues ?? {};

      const incomingFieldValue =
        readValueByName(values ?? {}, name) ?? readValueByName(prevSnapshot, name);

      let nextSnapshot = upsertValueByName(prevSnapshot, name, incomingFieldValue);

      let currentSections = candidateFields?.sections || [];
      const triggersIdRecalc = idRequirementsDependencyCodes.includes(name);

      // Hidden + dynamic for hidden keys (e.g. citizenship/dob → acic_primary_use_config_key).
      // Upload path uses recalculateIdRequirements below, which runs the same logic inside calculateIdRequirements.
      const hiddenDeps = getDependents(name, hiddenDependencyCodes);
      if (hiddenDeps.length) {
        const hiddenUpdates = runHiddenDependentsChained(
          hiddenDeps,
          nextSnapshot,
          hiddenFields,
          hiddenDependencyCodes,
        );
        if (hiddenUpdates) {
          dispatch(updateHiddenValues(hiddenUpdates));
          Object.assign(nextSnapshot, hiddenUpdates);

          const dynamicSections = runDynamicDependentsForHiddenUpdates(
            hiddenUpdates,
            currentSections,
            fieldPathIndex,
            nextSnapshot,
            dynamicDependencyCodes,
            templateSources,
          );
          if (dynamicSections) {
            currentSections = dynamicSections;
            dispatch(updateCandidateSections(dynamicSections));
          }
        }
      }

      const dynamicDeps = getDependents(name, dynamicDependencyCodes);
      if (dynamicDeps.length) {
        const updatedSections = runDynamicDependents(
          dynamicDeps,
          currentSections,
          fieldPathIndex,
          incomingFieldValue,
          nextSnapshot,
          templateSources,
        );
        if (updatedSections) {
          dispatch(updateCandidateSections(updatedSections));
        }
      }

      // Validators (section-local)
      {
        const lookupNames = [name];
        const segments = name.split('.');
        if (segments.length > 3) {
          lookupNames.push(segments.slice(0, 3).join('.'));
        }
        const deps = [
          ...new Set(
            lookupNames.flatMap(lookupName =>
              getDependentsWithIndexes(lookupName, validationDependencyCodes),
            ),
          ),
        ];

        if (deps.length) {
          runDependentValidators(
            deps,
            currentPagePrefixes,
            wrapperValidators,
            nextSnapshot,
            trigger,
            setError,
            clearErrors,
          );
        }
      }

      if (triggersIdRecalc) {
        dispatch(recalculateIdRequirements({ latestFormValues: nextSnapshot }));
      }
    });

    return () => subscription.unsubscribe();
  }, [
    watch,
    allValues,
    wrapperValidators,
    validationDependencyCodes,
    dynamicDependencyCodes,
    hiddenDependencyCodes,
    idRequirementsDependencyCodes,
    currentPagePrefixes,
    clearErrors,
    setError,
    trigger,
    dispatch,
  ]);
}
